import { calculatePrecision, toBig } from './number';

export type PrecisionCandidate = { increment?: string; value?: string };

function incrementFromDecimalPlaces(decimalPlaces: number): string {
  if (decimalPlaces <= 0) {
    return '1';
  }
  return `0.${'0'.repeat(decimalPlaces - 1)}1`;
}

/**
 * Decimal places implied by a displayed value.
 * `toBig().toFixed()` strips trailing zeros (`"10.20"` → `"10.2"`) */
function decimalPlacesFromValue(value: string | undefined): number | undefined {
  if (value == null || value === '') {
    return undefined;
  }
  const normalized = toBig(value)?.toFixed();
  if (normalized == null) {
    return undefined;
  }
  return calculatePrecision(normalized);
}

/**
 * Decimal places this candidate contributes to sibling alignment.
 * When both increment and value exist, use the coarser of the two so a value like `"100.01000"`
 * with increment `"0.00001"` aligns at 2 places after trailing zeros are stripped, not 5.
 */
function decimalPlacesForCandidate(candidate: PrecisionCandidate): number | undefined {
  const incrementPlaces = candidate.increment ? calculatePrecision(candidate.increment) : undefined;
  const valuePlaces = decimalPlacesFromValue(candidate.value);
  if (incrementPlaces == null) {
    return valuePlaces;
  }
  if (valuePlaces == null) {
    return incrementPlaces;
  }
  return Math.min(incrementPlaces, valuePlaces);
}

/**
 * Reuse the candidate's increment only when it already has exactly `places` decimals.
 */
function incrementMatchingPlaces(candidate: PrecisionCandidate, places: number): string | undefined {
  if (candidate.increment == null || calculatePrecision(candidate.increment) !== places) {
    return undefined;
  }
  return candidate.increment;
}

/**
 * Shared increment for sibling size cells: fewest decimal places across candidates.
 * Prefers an original increment string that already matches that place count (e.g. `"0.05"`);
 * otherwise synthesizes a power-of-ten increment.
 */
export function getCoarsestIncrement(candidates: PrecisionCandidate[]): string | undefined {
  let minPlaces: number | undefined;
  let incrementAtMinPlaces: string | undefined;
  for (const candidate of candidates) {
    const places = decimalPlacesForCandidate(candidate);
    if (places == null) {
      continue;
    }
    if (minPlaces == null || places < minPlaces) {
      minPlaces = places;
      incrementAtMinPlaces = incrementMatchingPlaces(candidate, places);
    } else if (places === minPlaces && incrementAtMinPlaces == null) {
      incrementAtMinPlaces = incrementMatchingPlaces(candidate, places);
    }
  }
  if (minPlaces == null) {
    return undefined;
  }
  return incrementAtMinPlaces ?? incrementFromDecimalPlaces(minPlaces);
}
